/* eslint-disable react/prop-types */
/**
 * MiniRiskGauge — a single-risk "Current state" graphic: where this risk currently sits between its
 * Worst Case and Best Case (0 = worst, 1 = best). When `onProximityChange` is supplied the ball is
 * draggable to OVER-RIDE the automated (AI) position (ID196 — replaces the separate manual slider).
 *
 * ID214 amendments: Best/Worst caps show the SAME qualitative consequence as the table (not a $);
 * the readout is the position % toward the NEARER pole only (no $ figure); the bar is coloured blue
 * ABOVE $0 (gain) and red BELOW $0 (loss), with a legend; the $0 marker is a clean break-even line.
 */
import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import MDTypography from "components/MDTypography";

const THREAT = "#ef4444";
const OPP = "#2563eb";
const INK = "#344767";
const MUTED = "#7b809a";

const fmtMoney = (v) => {
  const n = Number(v || 0);
  if (!n) return "$0";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(0)}k`;
  return `${sign}$${abs.toFixed(0)}`;
};

// Qualitative consequence text for a scenario row — matches the Scenario-assessment table's
// "<Type> <Level>" (e.g. "Regulatory Compliance Level 2"). Falls back to the signed $ when unset.
const qualText = (r, signed, magnitude) => {
  const t = `${(r && r.consequenceType) || ""} ${(r && r.qualitative) || ""}`.trim();
  if (t) return t;
  if (typeof signed === "number" && signed) return fmtMoney(signed);
  return magnitude ? fmtMoney(magnitude) : "";
};

// SVG geometry — just the bar + ball + $0 line + % readout; the caps are HTML (so long qualitative
// text wraps instead of overflowing the narrow SVG).
const W = 170;
const H = 160;
const barX = 85; // centred (W/2) so the bar lines up under the centred caps + heading (ID196)
const top = 14;
const bot = H - 14;

export default function MiniRiskGauge({
  scenarioRows = [],
  proximity,
  direction,
  bestSigned, // signed $ of the Best Case (+gain / −loss); optional
  worstSigned, // signed $ of the Worst Case (usually −loss); optional
  onProximityChange, // (prox) => void — when set, the ball is draggable to override the AI position
}) {
  const svgRef = useRef(null);
  const [dragProx, setDragProx] = useState(null);
  const draggingRef = useRef(false);

  const aiProx = typeof proximity === "number" ? Math.max(0, Math.min(1, proximity)) : 0.5;
  const prox = dragProx != null ? dragProx : aiProx;
  const editable = typeof onProximityChange === "function";

  const bestRow = scenarioRows.find((s) => /best/i.test(s.label)) || scenarioRows[0];
  const worstRow = scenarioRows.find((s) => /worst/i.test(s.label)) || scenarioRows[1];
  const vals = scenarioRows.map((s) => Number(s.value) || 0).filter((v) => v > 0);
  const minV = vals.length ? Math.min(...vals) : 0;
  const maxV = vals.length ? Math.max(...vals) : 0;
  const bestCap = qualText(bestRow, bestSigned, minV);
  const worstCap = qualText(worstRow, worstSigned, -maxV);

  const ballY = bot - (bot - top) * prox; // prox=1 → top (best), prox=0 → bottom (worst)

  // Position % toward the NEARER pole only (ID214): a ball at 0.74 reads "74% to best case".
  const pctBest = Math.round(prox * 100);
  const nearBest = prox >= 0.5;
  const pct = nearBest ? pctBest : 100 - pctBest;
  const nearerLabel = nearBest ? "to best case" : "to worst case";

  // $0 crossing (prox at which the signed value is 0), and whether the whole bar is one side of $0.
  const haveSigned = typeof bestSigned === "number" && typeof worstSigned === "number";
  let p0 = null; // proximity at $0
  if (haveSigned && bestSigned !== worstSigned) {
    const t = -worstSigned / (bestSigned - worstSigned);
    if (t >= 0 && t <= 1) p0 = t;
  }
  const zeroY = p0 != null ? bot - (bot - top) * p0 : null;
  const zeroFrac = p0 != null ? 1 - p0 : null; // gradient offset (0 = top/best)
  const bothGain = haveSigned && bestSigned >= 0 && worstSigned >= 0;
  // Signed value under the ball → ball is a "gain" (blue) only when above $0.
  const signedAtBall = haveSigned ? worstSigned + (bestSigned - worstSigned) * prox : null;
  const ballAboveZero = signedAtBall != null ? signedAtBall >= 0 : nearBest;
  const ballColor = ballAboveZero ? OPP : THREAT;

  // Bar gradient (top→bottom): blue above $0, red below. When all one side, a single-colour gradient.
  let stops;
  if (zeroFrac != null) {
    const z = Math.max(0, Math.min(1, zeroFrac));
    stops = [
      { o: 0, c: "#2563eb" },
      { o: z, c: "#93c5fd" },
      { o: z, c: "#fca5a5" },
      { o: 1, c: "#b71c1c" },
    ];
  } else if (bothGain) {
    stops = [
      { o: 0, c: "#2563eb" },
      { o: 1, c: "#93c5fd" },
    ];
  } else {
    stops = [
      { o: 0, c: "#fca5a5" },
      { o: 1, c: "#b71c1c" },
    ];
  }
  const hasZero = zeroFrac != null;

  // ── Drag to override ──────────────────────────────────────────────────────────
  const yToProx = (clientY) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect || !rect.height) return prox;
    const vy = ((clientY - rect.top) / rect.height) * H;
    return Math.max(0, Math.min(1, (bot - vy) / (bot - top)));
  };
  useEffect(() => {
    if (!editable) return undefined;
    const move = (e) => {
      if (!draggingRef.current) return;
      const cy = e.touches ? e.touches[0].clientY : e.clientY;
      setDragProx(yToProx(cy));
    };
    const up = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      setDragProx((p) => {
        if (p != null) onProximityChange(p);
        return null;
      });
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, [editable, onProximityChange]);
  const startDrag = (e) => {
    if (!editable) return;
    e.preventDefault();
    draggingRef.current = true;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    setDragProx(yToProx(cy));
  };

  const capLabel = { fontWeight: 700, fontSize: "0.78rem", display: "block", lineHeight: 1.1 };
  const capText = { fontSize: "0.72rem", color: MUTED, display: "block", lineHeight: 1.2 };

  return (
    <Box sx={{ textAlign: "center", maxWidth: 230, mx: "auto" }}>
      {/* Best Case cap (HTML so long qualitative text wraps) */}
      <MDTypography sx={{ ...capLabel, color: "#2e7d32" }}>Best Case</MDTypography>
      {bestCap ? <MDTypography sx={capText}>{bestCap}</MDTypography> : null}

      <svg
        ref={svgRef}
        width="100%"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="Current state between worst and best case"
        style={{ touchAction: editable ? "none" : "auto", maxHeight: 180 }}
      >
        <defs>
          <linearGradient id="mrgBar" x1="0" y1="0" x2="0" y2="1">
            {stops.map((s) => (
              <stop
                key={`${s.o}-${s.c}`}
                offset={`${s.o * 100}%`}
                stopColor={s.c}
                stopOpacity="0.9"
              />
            ))}
          </linearGradient>
          <radialGradient id="mrgBall" cx="35%" cy="30%" r="75%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
            <stop offset="35%" stopColor={ballColor} />
            <stop offset="100%" stopColor={ballColor} />
          </radialGradient>
        </defs>

        {/* The scenario bar */}
        <rect x={barX - 7} y={top} width="14" height={bot - top} rx="7" fill="url(#mrgBar)" />

        {/* $0 break-even marker — a clean thin line + small label (ID214: no more ugly dashes) */}
        {zeroY != null && (
          <>
            <line
              x1={barX - 10}
              y1={zeroY}
              x2={barX + 10}
              y2={zeroY}
              stroke="#334155"
              strokeWidth="1"
            />
            <text x={barX + 14} y={zeroY + 3} textAnchor="start" fontSize="8.5" fill="#334155">
              $0
            </text>
          </>
        )}

        {/* The position ball (draggable when editable) */}
        <circle
          cx={barX}
          cy={ballY}
          r={editable ? 12 : 10}
          fill="url(#mrgBall)"
          stroke="#fff"
          strokeWidth="2"
          style={{ cursor: editable ? "grab" : "default" }}
          onMouseDown={startDrag}
          onTouchStart={startDrag}
        />
      </svg>

      {/* Worst Case cap */}
      <MDTypography sx={{ ...capLabel, color: "#b71c1c" }}>Worst Case</MDTypography>
      {worstCap ? <MDTypography sx={capText}>{worstCap}</MDTypography> : null}

      {/* Position % readout — centred below the bar (ID196: nearer pole only, no $) */}
      <MDTypography sx={{ fontWeight: 700, fontSize: "1rem", color: INK, mt: 0.75 }}>
        {pct}%{" "}
        <MDTypography component="span" sx={{ fontSize: "0.72rem", color: MUTED, fontWeight: 400 }}>
          {nearerLabel}
        </MDTypography>
      </MDTypography>

      {/* Colour key (ID214) */}
      {hasZero && (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5, mt: 0.75 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: OPP }} />
            <MDTypography sx={{ fontSize: 10, color: MUTED }}>Gain (above $0)</MDTypography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: "#b71c1c" }} />
            <MDTypography sx={{ fontSize: 10, color: MUTED }}>Loss (below $0)</MDTypography>
          </Box>
        </Box>
      )}

      <MDTypography variant="caption" sx={{ color: MUTED, display: "block", mt: 0.5 }}>
        {direction ? `Current state: ${direction}` : "Current state"}
      </MDTypography>
      {editable && (
        <MDTypography
          variant="caption"
          sx={{ color: "#8a6300", display: "block", mt: 0.25, fontStyle: "italic" }}
        >
          Grab and drag the ball to over-ride the automated assessment.
        </MDTypography>
      )}
    </Box>
  );
}
